// src/pages/CancelGrant.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css"; // Reusing your existing styling
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

export default function CancelGrant() {
  const navigate = useNavigate();
  useEscapeToGoBack();

  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  const [mmyy, setMmyy] = useState("");
  const [receiptTail, setReceiptTail] = useState("");

  const [view, setView] = useState(null);
  const [error, setError] = useState("");

  const [confirm1, setConfirm1] = useState(null);
  const [confirm2, setConfirm2] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- helpers ----
  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const pad4 = (s = "") => onlyDigits(s).padStart(4, "0");
  const detectSeries = (tailDigits) => (tailDigits.length === 5 ? "S" : "B");

  const canonicalReceipt = () => {
    const mmYY = pad4(mmyy);
    const tail = onlyDigits(receiptTail);
    const series = detectSeries(tail);
    const tailNorm = series === "B" ? tail.padStart(4, "0") : tail; 
    return `${series}/${mmYY}/${tailNorm}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  // ---- focus behavior ----
  useEffect(() => {
    mmyyRef.current?.focus();
  }, []);

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
    }
  };

  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // ---- actions ----
  const handleSearch = async () => {
    setError("");
    setView(null);
    setConfirm1(null);
    setConfirm2(null);

    if (!mmyy || !receiptTail) {
      setError("Please fill MMYY and Receipt Number.");
      return;
    }

    try {
      const receiptNo = canonicalReceipt();
      const res = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/by-receipt",
        { params: { receiptNo } }
      );
      setView(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || "Grant transaction not found!");
    }
  };

  const onFirstConfirm = (ans) => {
    setConfirm1(ans === "yes");
    if (ans !== "yes") setConfirm2(false);
  };

  const onSecondConfirm = async (ans) => {
    setConfirm2(ans === "yes");
    if (ans !== "yes") return;
    try {
      setIsSubmitting(true);
      const receiptNo = view?.receiptNo || canonicalReceipt();
      
      // Changed to axios.delete
      await axios.delete(
        "https://www.lianfoong.com/api/pawn-transactions/cancel-grant",
        { params: { receiptNo } }
      );
      
      alert("Grant permanently deleted.");
      navigate("/dashboard");
    } catch (e) {
      alert(e?.response?.data || "Deletion failed.");
      setIsSubmitting(false);
    }
  };
  // ---- UI ----
  return (
    <div className="renew-container">
      <h2 className="title">Cancel – Grant</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(onlyDigits(e.target.value))}
          onKeyDown={onMmyyKeyDown}
          placeholder="MMYY"
          inputMode="numeric"
          autoComplete="off"
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          ref={receiptRef}
          value={receiptTail}
          onChange={(e) => setReceiptTail(onlyDigits(e.target.value))}
          onKeyDown={onReceiptKeyDown}
          placeholder="0001 or 80001"
          inputMode="numeric"
          autoComplete="off"
        />
      </div>

      <button className="search-btn" type="button" onClick={handleSearch}>
        Search
      </button>
      {error && <p className="error-message">{error}</p>}

      {view && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Receipt:</strong> {view.receiptNo}</p>
            <p><strong>Amount:</strong> ${Number(view.amount).toFixed(2)}</p>
            <p><strong>Grant Date:</strong> {fmtDate(view.grantDate)}</p>
            <p><strong>Item:</strong> {view.item || "-"}</p>
            <p><strong>Weight:</strong> {view.weight || "-"}</p>
            <p><strong>Status:</strong> {view.activeRecord ? "Active" : "Inactive"}</p>
          </div>

          {confirm1 === null && (
            <div className="alert-box">
              <p><strong>Are you sure you want to cancel this grant?</strong></p>
              <button onClick={() => onFirstConfirm("yes")}>Yes</button>
              <button onClick={() => onFirstConfirm("no")}>No</button>
            </div>
          )}

          {confirm1 === true && confirm2 === null && (
            <div className="alert-box">
              <p><strong>Please re-confirm:</strong></p>
              <button disabled={isSubmitting} onClick={() => onSecondConfirm("yes")}>
                {isSubmitting ? "Cancelling..." : "Yes"}
              </button>
              <button disabled={isSubmitting} onClick={() => onSecondConfirm("no")}>
                No
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}